<template>
  <button
    class="flex items-center justify-center relative gap-1 transition"
    :class="[sizeClasses, colorClasses, disabledClasses, iconClasses, loadingClasses]"
  >
    <l-icon
      v-if="loading"
      size="small"
      name="status"
      class="animate-spin absolute inset-0 m-auto z-10"
    />
    <l-icon
      v-if="icon"
      :size="isOnlyIcon ? size : 'small'"
      :name="icon"
      :class="{ invisible: loading }"
    />
    <span
      class="flex items-center justify-center gap-1 leading-none"
      v-if="$slots.default"
      :class="{ invisible: loading }"
    >
      <slot></slot>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'

import type { LIconName } from '@last/core-ui/paprika'
import { LIcon } from '@last/core-ui/paprika'

interface Props {
  /**
   * Button type
   * @default primary
   * */
  type?: 'primary' | 'secondary' | 'destructive' | 'alternative' | 'vertical'
  /**
   * Button size
   * @default medium
   * */
  size?: 'small' | 'medium' | 'large'
  /**
   * Show a loading spinner and disable the button
   * @default false
   */
  loading?: boolean
  /**
   * Disable the button
   * @default false
   */
  disabled?: boolean
  /**
   * Icon alignment
   * @default left
   * */
  iconAlign?: 'left' | 'right'
  /**
   * Icon name
   * */
  icon?: LIconName
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  type: 'primary',
  loading: false,
  disabled: false,
  iconAlign: 'left',
  icon: undefined
})

const slots = useSlots()

const isOnlyIcon = computed<boolean>(() => {
  return !!props.icon && !slots.default
})

const sizeClasses = computed<string[]>(() => {
  const common = ['rounded-lg', 'hover:scale-[1.01]']
  if (props.type === 'vertical') return ['h-[4.75rem]', 'flex-col', 'w-full', ...common]
  if (isOnlyIcon.value) {
    if (props.size === 'small') return ['h-9', 'w-9', ...common]
    if (props.size === 'large') return ['h-14', 'w-14', ...common]
    return ['h-11', 'w-11', ...common]
  }
  if (props.size === 'small') return ['body-3', 'h-9', 'px-2', ...common]
  if (props.size === 'large') return ['body-1', 'h-14', 'px-4', ...common]
  return ['body-2', 'h-11', 'px-4', ...common]
})

const colorClasses = computed<string[]>(() => {
  const colors: Record<typeof props.type, string[]> = {
    primary: ['bg-v-300', 'hover:bg-v-400'],
    secondary: ['bg-secondary', 'hover:bg-secondary-hover'],
    destructive: ['bg-r-300', 'hover:bg-r-400'],
    alternative: ['bg-g-600', 'hover:bg-g-600/90'],
    vertical: ['bg-secondary', 'hover:bg-secondary/90']
  }

  return [
    'text-n-0',
    'shadow-[0_0_16x_0_rgba(0,0,0,0.25),0_4px_8px_0_rgba(0,0,0,0.20)]',
    'hover:shadow-[0_2px_20px_0_rgba(0,0,0,0.25),0_6px_12px_0_rgba(0,0,0,0.20)]',
    'active:shadow-[0_0x_8px_0_rgba(0,0,0,0.25),0_2px_4px_0_rgba(0,0,0,0.20)]',
    ...colors[props.type]
  ]
})

const disabledClasses = computed<string[]>(() => {
  return props.disabled ? ['opacity-50', 'cursor-not-allowed', 'pointer-events-none'] : []
})

const iconClasses = computed<string[]>(() => {
  return props.iconAlign === 'right' ? ['row-reverse'] : []
})

const loadingClasses = computed<string[]>(() => {
  return props.loading ? ['opacity-50', 'cursor-wait', 'pointer-events-none'] : []
})
</script>
